// Libraries
import React, { useState, useEffect, useRef } from 'react'
import { Formik, Form } from 'formik'
import { Button, message, Modal } from 'antd'
import PropTypes from 'prop-types'
// Components
import { Tags } from '../../../EditProfile/Tags'
import Select from '../../../Base/Forms/Select'
// Styles
import '../../../EditProfile/Modals/Qualifications/styles.css'
// Styled Components
import { ErrorText } from '../../../EditProfile/Inputs/styles'
import { WrapperInputSkills } from '../../../EditProfile/Modals/styles'
import { ButtonContainer } from '../../../Base/Forms/styles'
// Hooks
import { useGetToken } from '../../../../hooks/useGetToken'
import { useInputValue } from '../../../../hooks/useInputValue'
import { useSave } from '../../../../Context/saveContext'
// Api
import { epagAPI } from '../../../../API/epagAPI'
// Utils
import {
  removeSkillAndUpdateSaved,
  setMessageWarning,
  tagsMapper,
  updateSkillsByMode,
  sortSingleValuesList,
  groupVariousCollections
} from '../../../../utils/utils'
import { AddNotFound } from '../../../EditProfile/AddNotFound'
import { SoftSkills, TechnicalSkills } from '../../../../constants/constants'
// Constants
import { Text } from '../../../../constants/TextConstants'
import { MODELS_GENERIC_MODAL } from '../../../../constants/modals'
import levels from '../../../../constants/levels'
import levelsTooltip from '../../../../constants/levelsTooltip'
import { SKILL_LEVEL_SCHEMA } from './schema'

const withoutSkills = ['N/A']
const INITIAL_FORM_VALUES = { skill: null, level: null }

const SkillLevelModal = ({
  open,
  setModal,
  setModelProfile,
  title,
  initialState = [],
  mode,
  subtitle,
  skillName,
  allowAdd = true,
  showDeleteOption = true,
  applyLevel,
  threshold = 0
}) => {
  const { confirm } = Modal
  const { token } = useGetToken()
  const newSkillValue = useInputValue('')
  const ErrorRequest = useInputValue(false)
  const { setIsSaved } = useSave()
  const [currentSkills, setCurrentSkills] = useState([])
  const [isSavingNewSkill, setIsSavingNewSkill] = useState(false)
  const [sortedSkillList, setSortedSkillList] = useState([])
  const [schema, setSchema] = useState(null)
  const [searchValue, setSearchValue] = useState(null)
  const formikRef = useRef()

  useEffect(() => {
    const updatedSchema = SKILL_LEVEL_SCHEMA(initialState, currentSkills, searchValue)
    setSchema(updatedSchema)
  }, [searchValue, currentSkills, initialState])

  useEffect(() => {
    setCurrentSkills(initialState)
  }, [initialState])

  useEffect(() => {
    if (token !== null && open === true && sortedSkillList.length === 0) {
      epagAPI
        .GetSkills(skillName, token)
        .then((response) => {
          if (response.length > 0) {
            const groupedSkills = groupVariousCollections(response)
            const list = updateSkillsByMode(mode, groupedSkills, withoutSkills)
            ErrorRequest.setErrorText('')
            ErrorRequest.setError(false)

            const sortedList = sortSingleValuesList(list)
            setSortedSkillList(sortedList)
          } else {
            setSortedSkillList([''])
          }
        })
        .catch((error) => {
          ErrorRequest.setErrorText(error.message)
          ErrorRequest.setError(true)
        })
    }
  }, [open, token, sortedSkillList])

  const addNewSkill = (newSkill, setFieldValue) => {
    if (newSkill.trim() !== '') {
      const skillAlreadyExists = sortedSkillList.some((item) => item === newSkill)
      if (!skillAlreadyExists) {
        setIsSavingNewSkill(true)
        epagAPI
          .CreateSkill(skillName, newSkill, token)
          .then((response) => {
            if (response && response.values) setSortedSkillList(sortSingleValuesList(response.values))
            if (!applyLevel) {
              setCurrentSkills([...currentSkills, { skill_name: newSkill, level: '' }])
            } else {
              setFieldValue('skill', newSkill)
            }
            setSearchValue('')
            setSortedSkillList(sortSingleValuesList([...sortedSkillList, newSkill]))
            ErrorRequest.setErrorText('')
            ErrorRequest.setError(false)
            message.success(Text.NewItemAdded)
          })
          .catch((error) => {
            ErrorRequest.setErrorText(error.message)
            ErrorRequest.setError(true)
          })
          .finally(setIsSavingNewSkill(false))
      } else {
        setMessageWarning(Text.ErrorAddingItem, newSkill)
      }
    } else {
      setMessageWarning(Text.ErrorEmptyInput, Text.SoftSkills)
    }
  }

  const showAddConfirm = (event, setFieldValue, resetForm) => {
    event.stopPropagation()
    confirm({
      title: `${Text.AddAlertItemMessage}: ${searchValue}?`,
      centered: true,
      icon: '',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk() {
        addNewSkill(searchValue, setFieldValue)
        resetForm()
      },
      onCancel() {
        setSearchValue('')
      }
    })
  }

  const deleteItem = (item) => {
    deleteSkill(item)
    newSkillValue.setValue('')
    setSortedSkillList([])
  }

  const onCancelDeleteItem = () => {
    newSkillValue.setValue('')
  }

  const showDeleteConfirm = (event, item, resetForm) => {
    event.stopPropagation()
    confirm({
      title: Text.DeleteAlertItemMessage,
      centered: true,
      icon: '',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk() {
        deleteItem(item)
        resetForm()
      },
      onCancel() {
        onCancelDeleteItem()
      }
    })
  }

  const info = (value) => {
    Modal.info({
      title: `The value "${value}" was deleted`,
      icon: '',
      centered: true,
      onOk() {}
    })
  }

  const deleteSkill = (value) => {
    epagAPI
      .DeleteSkill(skillName, value, token)
      .then((response) => {
        const skillsByMode = updateSkillsByMode(mode, response, [])
        const list = skillsByMode.filter((skillVal) => skillVal !== value)
        setSortedSkillList(sortSingleValuesList(list))
        info(value)
      })
      .catch((error) => {
        message.success(error.message, 3)
      })
  }

  const setSkillLevelState = (newSkill) => {
    if (newSkill && !currentSkills.some((skill) => skill.skill_name === newSkill.skill_name)) {
      setCurrentSkills((prevState) => [...prevState, newSkill])
    } else if (newSkill) {
      setMessageWarning(Text.ErrorAddingItem, newSkill)
    }
  }

  const handleRemoveSkill = (index) => {
    const updatedSkills = removeSkillAndUpdateSaved(index, currentSkills, initialState, setIsSaved)
    setCurrentSkills(updatedSkills)
  }

  const onSubmit = () => {
    setModelProfile((prevState) => {
      let copyState = { ...prevState }
      const profileMode = MODELS_GENERIC_MODAL[mode]
      profileMode ? (copyState[MODELS_GENERIC_MODAL[mode]] = currentSkills) : (copyState = currentSkills)
      return copyState
    })
    setIsSaved(false)
    setModal(false)
  }

  const onCancel = () => {
    formikRef?.current?.resetForm()
    setCurrentSkills(initialState)
    setModal(false)
  }

  const onChangeSkill = (value) => {
    const existingSkill = currentSkills.find((skill) => skill.skill_name === value)
    if (!existingSkill) {
      const newSkill = {
        skill_name: value,
        level: ''
      }
      setSkillLevelState(newSkill, true)
    }
  }

  const onChangeLevel = (value, skill) => {
    const newSkill = {
      skill_name: skill,
      level: value
    }
    setSkillLevelState(newSkill, true)
  }

  const onDragTags = (tags) => {
    const transformedTags = tags.map(({ name, level }) => ({
      skill_name: name,
      level
    }))
    setCurrentSkills(transformedTags)
  }

  const getOptionsFromArray = (array, tooltips = [], optionToBeFilter = []) =>
    array
      ? array
        .filter((item) => !optionToBeFilter.find((e) => e.skill_name === item))
        .map((item) => ({ value: item, label: item, tooltipText: tooltips[item] }))
      : []

  return (
    <Modal
      okText={Text.Add}
      title={title}
      open={open}
      footer={null}
      onCancel={onCancel}
      maskClosable={false}
      keyboard={false}>
      <Formik
        initialValues={INITIAL_FORM_VALUES}
        validationSchema={schema}
        initialTouched={{ skill: true, level: true }}
        onSubmit={(values, { resetForm }) => {
          onSubmit(values)
          resetForm()
        }}
        innerRef={formikRef}>
        {({ isValid, values, resetForm, handleSubmit, setFieldValue }) => {
          return (
            <Form>
              <WrapperInputSkills spaceStyle={applyLevel ? 'space-around' : ''}>
                <Select
                  name='skill'
                  showSearch
                  onSearch={(value) => setSearchValue(value)}
                  label={subtitle}
                  options={getOptionsFromArray(sortedSkillList, [], currentSkills)}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  showDeleteOption={showDeleteOption}
                  onDeleteOption={showDeleteConfirm}
                  onChange={(value) => {
                    if (!applyLevel) {
                      onChangeSkill(value)
                      resetForm()
                    } else setFieldValue('skill', value)
                  }}
                  resetForm={resetForm}
                  notFoundContent={
                    <AddNotFound
                      allowAdd={allowAdd}
                      isSaving={isSavingNewSkill}
                      addNewSkill={(e) => showAddConfirm(e, setFieldValue, resetForm)}
                    />
                  }
                />
                {!!applyLevel && (
                  <Select
                    name='level'
                    label='Level'
                    options={getOptionsFromArray(levels, levelsTooltip)}
                    onChange={(value) => {
                      onChangeLevel(value, values.skill)
                      resetForm()
                    }}
                    disabled={values && !values.skill}
                  />
                )}
              </WrapperInputSkills>
              <Tags
                data={tagsMapper(currentSkills)}
                threshold={threshold}
                categoryName={TechnicalSkills}
                onDragTags={onDragTags}
                isDragDisable={skillName === SoftSkills ? true : undefined}
                removeItemFunc={(index) => {
                  resetForm()
                  handleRemoveSkill(index)
                }}
              />
              {!!ErrorRequest.ErrorValue && <ErrorText>{ErrorRequest.ErrorText}</ErrorText>}
              <ButtonContainer>
                <Button onClick={onCancel} style={{ marginRight: '10px' }}>
                  Cancel
                </Button>
                <Button type='primary' onClick={() => handleSubmit()} disabled={!isValid}>
                  Add
                </Button>
              </ButtonContainer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

SkillLevelModal.propTypes = {
  open: PropTypes.bool,
  setModal: PropTypes.func,
  setModelProfile: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  initialState: PropTypes.array,
  mode: PropTypes.string,
  subtitle: PropTypes.string,
  skillName: PropTypes.string,
  applyLevel: PropTypes.bool,
  allowAdd: PropTypes.bool,
  showDeleteOption: PropTypes.bool,
  threshold: PropTypes.number
}

export default SkillLevelModal
